/**
 * BackupSelectionFloatingButton Component
 *
 * A draggable floating button that appears when the user minimizes the ticket
 * request wizard. Shows "Re-Open Ticket Request" with a glow effect.
 * Can be moved around the screen like a non-modal.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Ticket, X, GripHorizontal } from 'lucide-react';
import { Portal } from '@shared/components/ui/Portal';
import { useHMCEventsStore, type HMCEvent } from '../hmcEventsStore';

interface BackupSelectionFloatingButtonProps {
  /** All events (needed to look up event names) */
  allEvents: HMCEvent[];
  /** Callback when user clicks to restore the modal */
  onRestore: () => void;
}

export const BackupSelectionFloatingButton: React.FC<BackupSelectionFloatingButtonProps> = ({
  allEvents,
  onRestore,
}) => {
  const {
    backupSelection,
    clearBackupSelection,
  } = useHMCEventsStore();

  // Dragging state
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef({ x: 0, y: 0, posX: 0, posY: 0 });

  // Initialize position from store when becoming visible
  useEffect(() => {
    if (backupSelection.isMinimized && backupSelection.minimizedPosition) {
      setPosition(backupSelection.minimizedPosition);
    } else if (backupSelection.isMinimized) {
      // Default to center-top if no position stored
      const centerX = Math.max(0, (window.innerWidth - 280) / 2);
      setPosition({ x: centerX, y: 20 });
    }
  }, [backupSelection.isMinimized, backupSelection.minimizedPosition]);

  /**
   * Handle drag start
   */
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    // Don't start drag from buttons
    if ((e.target as HTMLElement).closest('button')) return;

    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      posX: position.x,
      posY: position.y,
    };
    e.preventDefault();
  }, [position]);

  /**
   * Handle drag move
   */
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragStartRef.current.x;
      const deltaY = e.clientY - dragStartRef.current.y;

      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - 280, dragStartRef.current.posX + deltaX)),
        y: Math.max(0, Math.min(window.innerHeight - 60, dragStartRef.current.posY + deltaY)),
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Only show when minimized and has a primary selection
  if (!backupSelection.isMinimized || !backupSelection.primaryEventId) {
    return null;
  }

  // Handle cancel - clear selection
  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    clearBackupSelection();
  };

  // Handle restore
  const handleRestore = () => {
    onRestore();
  };

  return (
    <Portal>
      <div
        ref={dragRef}
        style={{
          position: 'fixed',
          left: position.x,
          top: position.y,
          zIndex: 350,
          cursor: isDragging ? 'grabbing' : 'auto',
        }}
        className="animate-in fade-in slide-in-from-top-2 duration-200"
        role="complementary"
        aria-label="Ticket request minimized"
      >
        <div
          className="bg-dark-surface border-2 border-brand-500 rounded-xl shadow-2xl overflow-hidden"
          style={{
            boxShadow: '0 0 20px rgba(99, 102, 241, 0.5), 0 0 40px rgba(99, 102, 241, 0.3), 0 4px 20px rgba(0, 0, 0, 0.5)',
          }}
        >
          {/* Draggable header area */}
          <div
            className="flex items-center gap-2 px-3 py-2 bg-brand-500/20 cursor-grab select-none border-b border-brand-500/30"
            onMouseDown={handleDragStart}
          >
            <GripHorizontal size={14} className="text-brand-400" />
            <Ticket size={16} className="text-brand-400" />
            <span className="text-xs text-brand-300 font-medium">Ticket Request</span>

            {/* Cancel button in header */}
            <button
              onClick={handleCancel}
              className="ml-auto p-1 text-dark-muted hover:text-red-400 transition-all duration-150 rounded"
              title="Cancel request"
            >
              <X size={14} />
            </button>
          </div>

          {/* Main clickable area */}
          <button
            onClick={handleRestore}
            className="flex items-center gap-3 px-4 py-3 hover:bg-dark-surface/50 transition-all duration-150 w-full text-left min-h-[52px]"
          >
            {/* Glowing icon */}
            <div
              className="w-10 h-10 rounded-full bg-brand-500/30 flex items-center justify-center flex-shrink-0"
              style={{
                boxShadow: '0 0 15px rgba(99, 102, 241, 0.6)',
              }}
            >
              <Ticket size={20} className="text-brand-300" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white">
                Re-Open Ticket Request
              </p>
              <p className="text-xs text-dark-muted">
                Click to continue
              </p>
            </div>
          </button>
        </div>
      </div>
    </Portal>
  );
};

export default BackupSelectionFloatingButton;
